import React, { useState } from 'react';
import LockClockOutlinedIcon from '@mui/icons-material/LockClockOutlined';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { isLinkedinBlocked } from '../api';
import { openLinkedinLoginTab } from './LinkedinAccount';
import { openRealLinkedin, useLinkedin } from '../extensionBridge';
import { Box, Chip, CircularProgress, IconButton, InputAdornment, LinearProgress, MenuItem, Select, TextField, Tooltip, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Modal from '../../../components/Modal/Modal';
import { AppButton } from '../../../components/UI';

export const IconBox = ({ children, size = 38, tone = 'blue' }) => {
  const tones = {
    blue: { bg: '#ebf4fb', color: '#03407b' },
    green: { bg: '#f0fdf4', color: '#16a34a' },
    amber: { bg: '#fffbeb', color: '#b45309' },
    red: { bg: '#fef2f2', color: '#dc2626' },
    solid: { bg: '#03407b', color: '#fff' },
  };
  const t = tones[tone] || tones.blue;
  return (
    <Box sx={{ width: size, height: size, borderRadius: '10px', bgcolor: t.bg, color: t.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, '& svg': { fontSize: size * 0.5 } }}>
      {children}
    </Box>
  );
};

export const Panel = ({ children, sx = {} }) => (
  <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden', ...sx }}>{children}</Box>
);

export const PanelHeader = ({ icon, title, subtitle, action, tone }) => (
  <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #eef2f7', display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
    {icon && <IconBox size={34} tone={tone}>{icon}</IconBox>}
    <Box sx={{ flex: 1, minWidth: 200 }}>
      <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.25 }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: '0.76rem', color: '#64748b' }}>{subtitle}</Typography>}
    </Box>
    {action}
  </Box>
);

export const EmptyState = ({ icon, title, text, action }) => (
  <Box sx={{ py: 6, px: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
    <IconBox size={52}>{icon}</IconBox>
    <Typography sx={{ mt: 1.5, fontWeight: 800, color: '#0f172a' }}>{title}</Typography>
    {text && <Typography sx={{ mt: 0.5, fontSize: '0.82rem', color: '#64748b', maxWidth: 420 }}>{text}</Typography>}
    {action && <Box sx={{ mt: 2 }}>{action}</Box>}
  </Box>
);

export const ErrorBanner = ({ children }) =>
  children ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderRadius: '10px', border: '1px solid #fecaca', bgcolor: '#fef2f2', color: '#b91c1c', fontSize: '0.84rem' }}>
      <WarningAmberIcon sx={{ fontSize: 18 }} />
      {children}
    </Box>
  ) : null;

const tagLabel = (v) => (typeof v === 'string' ? v : `${v.technology || v.language || 'Compétence'}${v.level ? ` — ${v.level}` : ''}`);

export const Tags = ({ values, max, color = '#03407b', bg = '#ebf4fb', nowrap = false, empty = 'Non renseigné' }) => {
  const list = (Array.isArray(values) ? values : []).filter(Boolean);
  if (!list.length) return <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8' }}>{empty}</Typography>;
  const shown = max ? list.slice(0, max) : list;
  return (
    <Box sx={{ display: 'flex', flexWrap: nowrap ? 'nowrap' : 'wrap', gap: 0.5, overflow: 'hidden' }}>
      {shown.map((v, i) => (
        <Chip key={`${tagLabel(v)}-${i}`} size="small" label={tagLabel(v)} sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, borderRadius: '6px', bgcolor: bg, color, maxWidth: 220 }} />
      ))}
      {max && list.length > max && (
        <Tooltip title={list.slice(max).map(tagLabel).join(', ')}>
          <Chip size="small" label={`+${list.length - max}`} sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, borderRadius: '6px', bgcolor: '#f1f5f9', color: '#64748b' }} />
        </Tooltip>
      )}
    </Box>
  );
};

export const SectionTitle = ({ children }) => (
  <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.75 }}>{children}</Typography>
);

/** Search + sort toolbar used by the candidate and mission lists. */
export const ListToolbar = ({ query, onQuery, placeholder, sort, onSort, sortOptions, count, right }) => (
  <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eef2f7', display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
    <TextField
      size="small"
      placeholder={placeholder}
      value={query}
      onChange={(e) => onQuery(e.target.value)}
      sx={{ flex: 1, minWidth: 240, '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#f8fafc' } }}
      slotProps={{
        input: {
          startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} /></InputAdornment>,
          endAdornment: query ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => onQuery('')}><CloseIcon fontSize="small" /></IconButton>
            </InputAdornment>
          ) : null,
        },
      }}
    />
    {sortOptions && (
      <Select size="small" value={sort} onChange={(e) => onSort(e.target.value)} sx={{ minWidth: 150, borderRadius: '10px', fontSize: '0.84rem' }}>
        {sortOptions.map((o) => <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.84rem' }}>{o.label}</MenuItem>)}
      </Select>
    )}
    <Typography sx={{ fontSize: '0.8rem', color: '#64748b', whiteSpace: 'nowrap' }}>
      <Box component="span" sx={{ fontWeight: 800, color: '#0f172a' }}>{count}</Box> résultat{count > 1 ? 's' : ''}
    </Typography>
    {right}
  </Box>
);

export const Pager = ({ page, pages, pageSize, onPage, onPageSize, sizes = [10, 25, 50] }) => (
  <Box sx={{ px: 2, py: 1, borderTop: '1px solid #eef2f7', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography sx={{ fontSize: '0.74rem', color: '#64748b', mr: 0.5 }}>Par page</Typography>
      {sizes.map((s) => (
        <Chip
          key={s}
          size="small"
          label={s}
          onClick={() => onPageSize(s)}
          sx={{ height: 24, minWidth: 34, fontSize: '0.74rem', fontWeight: 700, borderRadius: '7px', cursor: 'pointer', bgcolor: pageSize === s ? '#03407b' : '#fff', color: pageSize === s ? '#fff' : '#475569', border: `1px solid ${pageSize === s ? '#03407b' : '#e2e8f0'}`, '&:hover': { bgcolor: pageSize === s ? '#02357a' : '#f0f7fd' } }}
        />
      ))}
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Typography sx={{ fontSize: '0.76rem', color: '#64748b' }}>Page {page} / {pages}</Typography>
      {[{ icon: <ChevronLeftIcon fontSize="small" />, to: page - 1, disabled: page <= 1 }, { icon: <ChevronRightIcon fontSize="small" />, to: page + 1, disabled: page >= pages }].map((b, i) => (
        <IconButton key={i} size="small" disabled={b.disabled} onClick={() => onPage(b.to)} sx={{ width: 30, height: 30, borderRadius: '8px', border: '1px solid #e2e8f0' }}>
          {b.icon}
        </IconButton>
      ))}
    </Box>
  </Box>
);

/** Confirmation with an optional free-text reason (used for deletions). */
export const ConfirmDialog = ({ open, title, text, confirmLabel = 'Confirmer', danger = false, busy = false, withReason = false, reason, onReason, onConfirm, onClose }) => (
  <Modal
    open={open}
    onClose={() => !busy && onClose()}
    title={title}
    maxWidth="xs"
    actions={
      <>
        <AppButton variant="secondary" onClick={onClose} disabled={busy}>Annuler</AppButton>
        <AppButton variant={danger ? 'danger' : 'primary'} loading={busy} onClick={onConfirm}>{confirmLabel}</AppButton>
      </>
    }
  >
    <Typography sx={{ fontSize: '0.86rem', color: '#334155', lineHeight: 1.6 }}>{text}</Typography>
    {withReason && (
      <TextField
        fullWidth
        multiline
        minRows={3}
        size="small"
        sx={{ mt: 2 }}
        label="Motif (facultatif)"
        placeholder="Précisez l'information incorrecte…"
        value={reason}
        onChange={(e) => onReason(e.target.value)}
      />
    )}
  </Modal>
);


/** Shown when LinkedIn refuses the collection: reconnect LinkedIn (this browser or the server). */
export const LinkedinBlockedHelp = ({ detail }) => {
  const [showDetail, setShowDetail] = useState(false);
  const li = useLinkedin();
  const inBrowser = !!li?.installed;
  return (
    <Box sx={{ p: 2, borderRadius: '12px', border: '1px solid #fde68a', bgcolor: '#fffbeb' }}>
      <Box sx={{ display: 'flex', gap: 1.25 }}>
        <IconBox size={34} tone="amber"><LockClockOutlinedIcon /></IconBox>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color: '#92400e' }}>LinkedIn demande une nouvelle connexion</Typography>
          <Typography sx={{ fontSize: '0.8rem', color: '#78350f', lineHeight: 1.55, mt: 0.25 }}>
            {inBrowser
              ? 'LinkedIn a demandé une connexion pendant la collecte. Connectez-vous sur linkedin.com dans ce navigateur, puis cliquez sur « Nouvel import ».'
              : 'La session LinkedIn du sourcing a expiré. Reconnectez LinkedIn, puis cliquez sur « Nouvel import ».'}
            {' '}Aucun profil n'a été modifié.
          </Typography>
          <Box sx={{ mt: 1.25, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <AppButton size="small" startIcon={<LinkedInIcon />} onClick={inBrowser ? openRealLinkedin : openLinkedinLoginTab}>
              {inBrowser ? 'Ouvrir linkedin.com' : 'Reconnecter LinkedIn'}
            </AppButton>
            <Box component="button" onClick={() => setShowDetail((v) => !v)} sx={{ p: 0, border: 'none', bgcolor: 'transparent', color: '#92400e', fontSize: '0.74rem', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit' }}>
              {showDetail ? 'Masquer le détail technique' : 'Détail technique'}
            </Box>
          </Box>
          {showDetail && <Typography sx={{ mt: 0.75, fontSize: '0.72rem', color: '#92400e', wordBreak: 'break-all' }}>{detail}</Typography>}
        </Box>
      </Box>
    </Box>
  );
};

/** Live progress of an import operation, step by step. */
export const PipelineSteps = ({ steps, stepStates, progress, logs, error }) => (
  <Box>
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {steps.map((step, i) => {
        const state = stepStates[step.id] || 'idle';
        const p = progress[step.id];
        return (
          <Box key={step.id} sx={{ display: 'flex', gap: 1.5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {state === 'done' ? (
                <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 22 }} />
              ) : state === 'error' ? (
                <CancelIcon sx={{ color: '#dc2626', fontSize: 22 }} />
              ) : state === 'running' ? (
                <CircularProgress size={20} thickness={5} sx={{ color: '#03407b', m: '1px' }} />
              ) : (
                <RadioButtonUncheckedIcon sx={{ color: '#cbd5e1', fontSize: 22 }} />
              )}
              {i < steps.length - 1 && <Box sx={{ width: 2, flex: 1, minHeight: 18, my: 0.5, borderRadius: 1, bgcolor: state === 'done' ? '#86efac' : '#e2e8f0' }} />}
            </Box>
            <Box sx={{ flex: 1, pb: i < steps.length - 1 ? 1.5 : 0 }}>
              <Typography sx={{ fontSize: '0.86rem', fontWeight: state === 'idle' ? 600 : 800, color: state === 'idle' ? '#94a3b8' : state === 'error' ? '#dc2626' : '#0f172a' }}>{step.label}</Typography>
              {state === 'running' && p?.total > 0 && (
                <Box sx={{ mt: 0.75, maxWidth: 520 }}>
                  <LinearProgress variant="determinate" value={Math.min(100, (p.current / p.total) * 100)} sx={{ height: 5, borderRadius: 3, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#03407b', borderRadius: 3 } }} />
                  <Typography sx={{ fontSize: '0.72rem', color: '#64748b', mt: 0.5 }}>{p.current}/{p.total}{p.detail ? ` — ${p.detail}` : ''}</Typography>
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
    {error && <Box sx={{ mt: 2 }}>{isLinkedinBlocked(error) ? <LinkedinBlockedHelp detail={error} /> : <ErrorBanner>{error}</ErrorBanner>}</Box>}
    {logs?.length > 0 && (
      <Box sx={{ mt: 2, p: 1.5, maxHeight: 150, overflowY: 'auto', borderRadius: '10px', bgcolor: '#0f172a', color: '#cbd5e1', fontFamily: 'monospace', fontSize: '0.72rem', lineHeight: 1.6 }}>
        {logs.slice(-12).map((l, i) => <div key={i}>{l}</div>)}
      </Box>
    )}
  </Box>
);
